import { secondaryMarketBatchApiConnector } from 'apiConnector/secondary-market/batch/api';
import { AvailableAction, Operation } from 'apiConnector/secondary-market/batch/base';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { AnalyticCategoryActions } from 'models';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrderType } from 'routes/secondary-market-batch/hooks/use-order-type';
import { lastValueFrom } from 'rxjs';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { secondaryMarketQuoteByIsinReloadRequested } from 'state/secondaryMarket/realtimeQuote/quote';
import { openInModifyCpOrderScreenThunk } from 'state/secondaryMarket/secondaryMarket.thunks';
import { secondaryMarketBatchActions } from 'state/workspaces/secondary-market/batch/actions';
import { getSelectedRows } from 'state/workspaces/secondary-market/batch/selectors';
import { httpClient } from 'utils/http/httpClient';

const api = secondaryMarketBatchApiConnector(httpClient);

const operationsOrder: Operation[] = [
  'open-in-secondary-market',
  'placement',
  'quote',
  'trade',
  'open-in-order-modification-screen',
];

const getAnalyticsActionByOperation: Record<
  Extract<Operation, 'placement' | 'quote' | 'trade'>,
  AnalyticCategoryActions['Secondary market batch']
> = {
  placement: 'Place orders',
  quote: 'Get quotes',
  trade: 'Confirm to trade',
};

function hasOrderId<Row extends { data: { orderId?: string } }>(row: Row): row is Row & { data: { orderId: string } } {
  return row.data.orderId !== undefined;
}

export function useActions() {
  const { orderType } = useOrderType();
  const dispatch = useThunkDispatch();
  const selectedRows = useAppSelector(state => getSelectedRows(state, orderType));
  const navigate = useNavigate();
  const selectedRowsIds = selectedRows.map(row => row.data.rowId);

  const operations = useMemo(() => {
    const actionsByType = Object.groupBy(
      selectedRows.flatMap(row => row.data.metadata.availableActions),
      action => action.type,
    );
    const allActions: AvailableAction[] = [];
    const validRowsByOperation: Partial<Record<Operation, number>> = {};
    for (const [type, actions] of Object.entries(actionsByType)) {
      if (actions.some(action => !action.enabled)) {
        allActions.push({ type: type as Operation, enabled: false });
      } else {
        allActions.push({ type: type as Operation, enabled: selectedRows.length === actions.length });
      }

      if (type !== 'open-in-secondary-market' && type !== 'open-in-order-modification-screen') {
        validRowsByOperation[type as Operation] = actions.filter(action => action.enabled).length;
      }
    }
    return {
      actions: allActions.sort((a, b) => {
        const aIndex = operationsOrder.indexOf(a.type);
        const bIndex = operationsOrder.indexOf(b.type);
        return aIndex - bIndex;
      }),
      validRowsByOperation,
    };
  }, [selectedRows]);

  return selectedRows.length === 0
    ? { selectedRowsCnt: selectedRows.length }
    : {
        orderType,
        selectedRowsCnt: selectedRows.length,
        resetSelectedRows() {
          const request = { rowIds: selectedRowsIds };
          dispatch(secondaryMarketBatchActions.resetRequested(orderType, request));
          api.reset(orderType, request).subscribe({
            next: response => {
              logAnalytics.trackEvent('Secondary market batch', 'Reset');
              dispatch(secondaryMarketBatchActions.resetSucceeded(orderType, response));
            },
            error: err => {
              dispatch(secondaryMarketBatchActions.resetFailed(orderType, selectedRowsIds, err));
            },
          });
        },
        deleteSelectedRows() {
          dispatch(secondaryMarketBatchActions.deleteRowsRequested(orderType, selectedRowsIds));
          lastValueFrom(api.delete(orderType, { rowIds: selectedRowsIds }))
            .then(() => {
              logAnalytics.trackEvent('Secondary market batch', 'Remove');
              dispatch(secondaryMarketBatchActions.deleteRowsSucceeded(orderType, selectedRowsIds));
            })
            .catch(error => {
              dispatch(secondaryMarketBatchActions.deleteRowsFailed(orderType, selectedRowsIds, error));
            });
        },
        async sendOperation(operation: Operation) {
          if (operation === 'open-in-secondary-market') {
            logAnalytics.trackEvent('Secondary market batch', 'Open in secondary market');

            if (orderType === 'ebiz-quote') {
              for (const row of selectedRows) {
                dispatch(secondaryMarketQuoteByIsinReloadRequested(row.data.isin, 'secondaryMarketTrades'));
              }
              return;
            }
            dispatch(
              secondaryMarketBatchActions.reloadOrders(
                orderType,
                selectedRows.map(row => row.data),
              ),
            );
            return;
          }
          if (operation === 'open-in-order-modification-screen') {
            const orderIds = selectedRows
              .filter(row =>
                row.data.metadata.availableActions.some(op => op.type === 'open-in-order-modification-screen'),
              )
              .filter(hasOrderId)
              .map(row => row.data.orderId);
            if (orderIds.length > 0) {
              await dispatch(openInModifyCpOrderScreenThunk(orderIds));
              navigate('/secondary-market-post-event-workspace');
            }
            return;
          }

          dispatch(secondaryMarketBatchActions.operationRequested(orderType, operation, selectedRowsIds));
          lastValueFrom(
            api.operation(orderType, operation, {
              rows: selectedRows.map(({ data }) => ({
                ...data,
                metadata: undefined,
                operationStatus: undefined,
              })),
            }),
          )
            .then(response => {
              logAnalytics.trackEvent('Secondary market batch', getAnalyticsActionByOperation[operation]);

              dispatch(
                secondaryMarketBatchActions.operationSucceeded(
                  orderType,
                  operation,
                  response.rows.map(r => ({
                    ...r,
                    metadata: {
                      ...r.metadata,
                      endZonedDateTimeToTrade: Temporal.Now.zonedDateTimeISO()
                        .add({ seconds: r.metadata?.timeToTradeInSeconds ?? 0 })
                        .toString(),
                    },
                  })),
                ),
              );
            })
            .catch(error => {
              dispatch(secondaryMarketBatchActions.operationFailed(orderType, operation, selectedRowsIds, error));
            });
        },
        availableOperations: operations.actions,
        validRowsByOperation: operations.validRowsByOperation,
      };
}
